/**
 * Connection Commands
 *
 * Commands for managing database connections from the command palette.
 */

import { ClipboardPaste, Container, Music, PackageOpen, PlugZap, Settings2 } from 'lucide-react';
import type { CommandDefinition } from '../types/commandTypes';
import { useCommandPaletteStore } from '../store/commandPaletteStore';
import { useConnectionsUIStore } from '../../connections/store/uiStore';
import { openSampleDatabase } from '../../sample-database';
import {
  IMPORT_SOURCES,
  isFileOnlySource,
  openAzureImportPicker,
  openImportDialog,
  openImportDialogViaBrowse,
} from '../../connections/connection-import';
import { openDockerDiscoveryDialog } from '../../connections/docker-discovery';
import { executeNewConnectionFromClipboard } from '../../connections/services/connection-string/newConnectionFromClipboard';
import { getCachedClipboardPreview } from '../../connections/services/connection-string/clipboardPreview';
import { isBrowserMode } from '../../browser-mode';

/** Close palette and run after a tick */
function closeAndRun(fn: () => void) {
  useCommandPaletteStore.getState().close();
  setTimeout(fn, 0);
}

export const connectionCommands: CommandDefinition[] = [
  {
    id: 'connection:new',
    label: 'New Connection',
    category: 'connection',
    icon: PlugZap,
    keywords: ['connect', 'server', 'add', 'new'],
    execute: () => closeAndRun(() => {
      useConnectionsUIStore.getState().setShowNewConnectionDialog(true);
    }),
  },
  {
    id: 'connection:new-from-clipboard',
    label: 'New Connection from Clipboard',
    category: 'connection',
    icon: ClipboardPaste,
    keywords: ['clipboard', 'paste', 'connection', 'string', 'url'],
    // Reads the clipboard on execute (never a background listener); the palette
    // shows a masked preview via `rightHint` when it parses as a connection
    // string. Desktop only — browser mode has no Electron clipboard bridge.
    isVisible: () => !isBrowserMode(),
    rightHint: () => getCachedClipboardPreview(),
    execute: () => closeAndRun(() => {
      void executeNewConnectionFromClipboard();
    }),
  },
  {
    id: 'connection:manage',
    label: 'Manage Connections',
    category: 'connection',
    icon: Settings2,
    keywords: ['connections', 'manage', 'edit', 'list'],
    execute: () => closeAndRun(() => {
      useConnectionsUIStore.getState().setShowConnectionsDialog(true);
    }),
  },
  // One "Import Connections from <Tool>" command per source, generated from the
  // registry. Reads the tool's store via IPC on execute; desktop only. When
  // nothing is found the dialog opens in its "nothing found — Browse…" empty
  // state rather than failing silently (file-only sources open the picker
  // directly).
  ...IMPORT_SOURCES.map((s): CommandDefinition => ({
    id: `connection:import-${s.id}`,
    label: `Import Connections from ${s.label}`,
    category: 'connection',
    icon: PackageOpen,
    keywords: ['import', 'migrate', 'connection', s.id, ...s.label.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean)],
    isVisible: () => !isBrowserMode(),
    execute: () =>
      closeAndRun(() => {
        if (s.id === 'azure') openAzureImportPicker('command_palette');
        else if (isFileOnlySource(s.id)) void openImportDialogViaBrowse(s.id, 'command_palette');
        else void openImportDialog(s.id, 'command_palette');
      }),
  })),
  {
    // Id kept as `detect-docker` on purpose — analytics/command history reference
    // it. Only the visible label follows the feature: detection now covers Docker
    // containers AND (on Windows) native SQL Server instances, so the label
    // mirrors the dialog's own product copy ("Detect local databases"), in the
    // Title Case this registry uses for palette rows.
    id: 'connection:detect-docker',
    label: 'Detect Local Databases',
    category: 'connection',
    icon: Container,
    keywords: [
      'docker', 'container', 'detect', 'local', 'postgres', 'mysql', 'discover',
      'sql server', 'sqlexpress', 'localdb', 'instance', 'native',
    ],
    // Queries the local Docker socket (and, on Windows, the native SQL Server
    // probe) via IPC on execute — explicit action only; desktop only. When
    // nothing is found the dialog opens in its empty / "Docker not found" state
    // rather than failing silently (palette rows can't tooltip, so the dialog
    // reports it).
    isVisible: () => !isBrowserMode(),
    execute: () => closeAndRun(() => {
      void openDockerDiscoveryDialog('command_palette');
    }),
  },
  {
    id: 'connection:open-sample-database',
    label: 'Open Sample Database',
    category: 'connection',
    icon: Music,
    keywords: ['sample', 'demo', 'chinook', 'sqlite', 'try', 'example'],
    // The desktop sample flow provisions a file under userData via IPC;
    // browser mode has its own demo-database picker instead.
    isVisible: () => !isBrowserMode(),
    execute: () => closeAndRun(() => {
      void openSampleDatabase('command_palette');
    }),
  },
];
